import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import { AppCard, AppHelpModal } from '@/layout/AppComponents';
import ReactDOMServer from 'react-dom/server';
import { Formik } from 'formik';
import BreadcrumbHandler from '../../../../layout/components/breadcrumb/BreadcrumbHandler';
import { axiosPath } from '../../../../routing/helpers/router';
import ObligationFormQuestionSwitch from '../../../../layout/screenSpecifics/ObligationForm/ObligationFormQuestionSwitch';
import ObligationFormCalcRow from '../../../../layout/screenSpecifics/ObligationForm/ObligationFormCalcRow';
import * as currentPageAction from "../../../../store/actions/currentPageAction";
import Translator from '../../../../layout/components/helpers/Translator';
import useIsMounted from '../../../../layout/components/hooks/useIsMounted';
import ClassifiedPlantSelect from '../../../../layout/components/form/Selects/ClassifiedPlantSelect';
import * as moduleAction from '../../../../store/actions/moduleAction';
import errorHandler from '@/layout/components/error/ErrorHandler';

export default function LegalCompliancePlantObligationFormManageScreen() {
  const { moduleBridgeHex, plantHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const permissions = useSelector(
    (s) => s.module?.[moduleBridgeHex]?.permissions
  );
  const [plantObligationForm, setPlantObligationForm] = useState();
  const [activeClassifiedPlants, setActiveClassifiedPlants] = useState([]);
  const userAnswers = useSelector((state) => state.currentPage?.answers);
  // > LOAD PLANT OBLIGATION FORM
  const loadUserAnswers = useCallback(async () => {
    axios
      .post(
        axiosPath(
          "api_r_user_m_legalCompliance_plantObligationForm_userAnswers_get"
        ),
        {
          moduleBridgeHex,
          plantHex,
        }
      )
      .then(async (r) => {
        if (isMounted.current) {
          if (!permissions) {
            await dispatch(
              moduleAction.fetch(
                axiosPath(
                  "api_r_user_module_legalCompliance_evaluation_getPermissions"
                ),
                "permissions",
                {
                  moduleBridgeHex,
                  plantHex,
                  subType: "permissions",
                }
              )
            );
          }
          dispatch(currentPageAction.change("answers", r.data.userAnswers));
          dispatch(
            currentPageAction.change(
              "pendingQuestions",
              r.data.pendingQuestions
            )
          );
        }
      })
      .catch((e) => dispatch(errorHandler(e)));
  }, [moduleBridgeHex, plantHex]);

  const loadPlantObligationForm = useCallback(async () => {
    await loadUserAnswers();
    axios
      .post(axiosPath("api_r_user_m_legalCompliance_plantObligationForm_get"), {
        moduleBridgeHex,
        plantHex,
      })
      .then(({ data }) => {
        if (isMounted.current) {
          setPlantObligationForm(data);
          setActiveClassifiedPlants(data?.activeClassifiedPlants ?? []);
        }
      })
      .catch((e) => dispatch(errorHandler(e)));
  }, [moduleBridgeHex, plantHex]);

  useEffect(() => {
    loadPlantObligationForm();
  }, [moduleBridgeHex, plantHex]);
  // < LOAD PLANT OBLIGATION FORM

  const handleUpdateClassifiedPlants = useCallback(
    async (payload) => {
      axios
        .post(
          axiosPath(
            'api_r_user_m_legalCompliance_plantObligationForm_classifiedPlants_update',
          ),
          {
            payload,
            moduleBridgeHex,
            plantHex,
          },
        )
        .then(({ data }) => {
          if (isMounted.current) {
            setActiveClassifiedPlants(data);
          }
        })
        .catch((e) => dispatch(errorHandler(e)));
    },
    [moduleBridgeHex, plantHex]
  );

  return (
    <Container>
      {plantObligationForm && (
        <BreadcrumbHandler
          paths={[
            {
              name: plantObligationForm?.breadcrumb?.plant?.name,
              route: {
                name: 'user_module_legalCompliance_evaluation',
                params: {
                  moduleBridgeHex,
                  plantHex,
                  page: 1,
                  filtersHex: 0,
                },
              },
            },
            {
              name: ReactDOMServer.renderToString(
                <Translator
                  file="module-legalCompliance"
                  name="obligations.form"
                />
              ),
            },
          ]}
        />
      )}

      {plantObligationForm?.questions?.length === 0 && (
        <div className="alert alert-info">
          <Translator
            file="module-legalCompliance"
            name="no.questions.found.form.contact.worldlex"
          />
        </div>
      )}
      {plantObligationForm &&
        permissions?.gcl?.module?.plant?.userObligationForm
          ?.classifiedPlantUpdate && (
          <AppCard
            header={ReactDOMServer.renderToString(
              <Translator
                file="module-legalCompliance"
                name="module.lc.classified-plants.selector"
              />
            )}
            className="mb-2"
          >
            <Formik
              initialValues={{
                classifiedPlants: plantObligationForm?.classifiedPlants ?? [],
              }}
              onSubmit={handleUpdateClassifiedPlants}
            >
              {({ setFieldValue, handleSubmit, values }) => (
                <form
                  onSubmit={handleSubmit}
                  className="d-flex align-items-center"
                >
                  <ClassifiedPlantSelect
                    value={values.classifiedPlants}
                    onChange={async (opt) => {
                      await setFieldValue('classifiedPlants', opt);
                      handleSubmit();
                    }}
                    isMulti
                    hasSelectOptions={false}
                    className="mt-2 mr-2"
                  />
                  <AppHelpModal
                    size="xl"
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-legalCompliance"
                        name="module.lc.classified-plants.selected"
                      />
                    )}
                    confirmBtnText={ReactDOMServer.renderToString(
                      <Translator
                        file="user"
                        name="user.accept"
                      />
                    )}
                  >
                    <ul>
                      {activeClassifiedPlants?.map((cp) => (
                        <li key={cp.id}>{cp.name}</li>
                      ))}
                    </ul>
                  </AppHelpModal>
                </form>
              )}
            </Formik>
          </AppCard>
        )}

      {plantObligationForm?.questions?.length > 0 && (
        <>
          <AppCard style={{ paddingBottom: '80px' }}>
            {plantObligationForm?.questions?.map((question) => (
              <React.Fragment key={question.id}>
                <ObligationFormQuestionSwitch question={question} />
                {question?.expandAnswerID &&
                  userAnswers?.[question?.id] &&
                  userAnswers?.[question?.id]?.filter(
                    (ans) => ans?.value === question?.expandAnswerID
                  )?.length > 0 &&
                  question?.subquestions?.map((subQuestion) => (
                    <ObligationFormQuestionSwitch
                      key={subQuestion.id}
                      question={subQuestion}
                      isSubquestion
                    />
                  ))}
              </React.Fragment>
            ))}
          </AppCard>
          <ObligationFormCalcRow />
        </>
      )}
    </Container>
  );
}

const Container = styled.div`
  margin-bottom: 80px;
`;
